import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { architectAPI, judgeAPI } from '../services/api';
import toast from 'react-hot-toast';

const COLORS = ['#F59E0B', '#10B981', '#3B82F6', '#EF4444'];

export default function Analytics() {
  const [stats, setStats] = useState({
    pendingQuestions: 0,
    approvedQuestions: 0,
    scoredArticles: 0,
    examDistribution: [],
    statusDistribution: [],
    dailyQuestions: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setStats(prev => ({ ...prev, loading: true, error: null }));
    try {
      // Fetch data from APIs
      const [pendingRes, approvedRes, judgeRes] = await Promise.all([
        architectAPI.getPendingQuestions().catch(e => ({ data: [] })),
        architectAPI.getApprovedQuestions().catch(e => ({ data: [] })),
        judgeAPI.getScoredArticles().catch(e => ({ data: { data: [] } })),
      ]);

      const pendingData = Array.isArray(pendingRes.data) ? pendingRes.data : [];
      const approvedData = Array.isArray(approvedRes.data) ? approvedRes.data : [];
      const articlesData = judgeRes.data?.data || [];

      // Exam distribution
      const examCounts = {};
      [...pendingData, ...approvedData].forEach(q => {
        if (q.exam) {
          examCounts[q.exam] = (examCounts[q.exam] || 0) + 1;
        }
      });
      const examDistribution = Object.entries(examCounts).map(([exam, count]) => ({
        exam,
        count,
      }));

      // Status distribution for pie chart
      const statusDistribution = [
        { name: 'Pending Review', value: pendingData.length },
        { name: 'Approved', value: approvedData.length },
      ];

      // Daily questions (last 7 days)
      const last7Days = Array.from({ length: 7 }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - (6 - i));
        return date.toISOString().split('T')[0];
      });

      const dailyQuestions = last7Days.map(date => {
        const count = approvedData.filter(q => 
          q.approved_at && q.approved_at.startsWith(date)
        ).length;
        return {
          date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          questions: count,
        };
      });

      setStats({
        pendingQuestions: pendingData.length,
        approvedQuestions: approvedData.length,
        scoredArticles: articlesData.length,
        examDistribution,
        statusDistribution,
        dailyQuestions,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics data');
      setStats(prev => ({ 
        ...prev, 
        loading: false,
        error: error.message || 'Failed to load data'
      }));
    }
  };

  if (stats.loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
          <p className="text-gray-600 font-medium">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (stats.error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center max-w-md">
          <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-xl mb-4">
            <p className="font-bold mb-2">Error Loading Analytics</p>
            <p className="text-sm">{stats.error}</p>
          </div>
          <button
            onClick={loadAnalytics}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Modern Header with Gradient */}
      <div className="relative bg-gradient-to-r from-purple-600 via-indigo-700 to-blue-800 rounded-3xl shadow-2xl p-8 mb-8 overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-3xl"></div>
        
        <div className="relative flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 shadow-xl">
              <svg className="h-12 w-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="text-white">
              <h2 className="text-3xl font-bold mb-1">Analytics Dashboard 📊</h2>
              <p className="text-white/90">
                System performance and insights at a glance
              </p>
            </div>
          </div>
          <button
            onClick={loadAnalytics}
            className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white px-6 py-3 rounded-xl font-bold shadow-lg transition-all hover:scale-105 flex items-center"
          >
            <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh Data
          </button>
        </div>
      </div>

      {/* Summary Cards with Modern Design */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <div className="bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 rounded-2xl shadow-xl p-6 text-white transform hover:scale-105 transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
              <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <span className="text-5xl font-bold">{stats.pendingQuestions}</span>
          </div>
          <h3 className="text-lg font-semibold mb-1">Pending Review</h3>
          <p className="text-white/80 text-sm">Questions awaiting approval</p>
        </div>

        <div className="bg-gradient-to-br from-green-400 via-emerald-500 to-teal-600 rounded-2xl shadow-xl p-6 text-white transform hover:scale-105 transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
              <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <span className="text-5xl font-bold">{stats.approvedQuestions}</span>
          </div>
          <h3 className="text-lg font-semibold mb-1">Approved</h3>
          <p className="text-white/80 text-sm">Production-ready questions</p>
        </div>

        <div className="bg-gradient-to-br from-blue-400 via-indigo-500 to-purple-600 rounded-2xl shadow-xl p-6 text-white transform hover:scale-105 transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
              <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <span className="text-5xl font-bold">{stats.approvedQuestions + stats.pendingQuestions}</span>
          </div>
          <h3 className="text-lg font-semibold mb-1">Total Questions</h3>
          <p className="text-white/80 text-sm">All generated questions</p>
        </div>

        <div className="bg-gradient-to-br from-pink-400 via-rose-500 to-red-600 rounded-2xl shadow-xl p-6 text-white transform hover:scale-105 transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
              <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
            </div>
            <span className="text-5xl font-bold">{stats.scoredArticles}</span>
          </div>
          <h3 className="text-lg font-semibold mb-1">Scored Articles</h3>
          <p className="text-white/80 text-sm">Relevance-checked content</p>
        </div>
      </div>

      {/* Charts with Modern Design */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 mb-6">
        {/* Exam Distribution */}
        <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-gray-100">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <span className="bg-blue-100 text-blue-600 rounded-lg p-2 mr-3">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </span>
            Questions by Exam Type
          </h3>
          {stats.examDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.examDistribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="exam" stroke="#6B7280" />
                <YAxis stroke="#6B7280" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#FFF', 
                    border: '2px solid #E5E7EB',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }} 
                />
                <Legend />
                <Bar dataKey="count" fill="#3B82F6" name="Questions" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <svg className="h-16 w-16 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <p className="font-medium">No exam data available</p>
              </div>
            </div>
          )}
        </div>

        {/* Status Distribution */}
        <div className="bg-white rounded-2xl shadow-xl p-6 border-2 border-gray-100">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <span className="bg-green-100 text-green-600 rounded-lg p-2 mr-3">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
              </svg>
            </span>
            Question Status Distribution
          </h3>
          {stats.statusDistribution.length > 0 && (stats.statusDistribution[0].value > 0 || stats.statusDistribution[1].value > 0) ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stats.statusDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {stats.statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#FFF', 
                    border: '2px solid #E5E7EB',
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                  }} 
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <svg className="h-16 w-16 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                </svg>
                <p className="font-medium">No status data available</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Daily Questions Trend */}
      <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border-2 border-gray-100">
        <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <span className="bg-purple-100 text-purple-600 rounded-lg p-2 mr-3">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
            </svg>
          </span>
          Daily Questions Generated (Last 7 Days)
        </h3>
        {stats.dailyQuestions.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={stats.dailyQuestions}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="date" stroke="#6B7280" />
              <YAxis stroke="#6B7280" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#FFF', 
                  border: '2px solid #E5E7EB',
                  borderRadius: '12px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }} 
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="questions" 
                stroke="#8B5CF6" 
                strokeWidth={3}
                name="Questions" 
                dot={{ fill: '#8B5CF6', r: 6 }}
                activeDot={{ r: 8 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <svg className="h-16 w-16 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
              </svg>
              <p className="font-medium">No trend data available</p>
            </div>
          </div>
        )}
      </div>

      {/* System Information - Modern Design */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 mt-6">
        {/* Services Status */}
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl shadow-xl p-6 border-2 border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <span className="bg-indigo-100 text-indigo-600 rounded-lg p-2 mr-3">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </span>
            Services Status
          </h3>
          <div className="space-y-3">
            {[
              { name: 'Ingestion Service', status: 'Operational', color: 'green' },
              { name: 'Extractor Service', status: 'Operational', color: 'green' },
              { name: 'Judge Agent Service', status: 'Operational', color: 'green' },
              { name: 'Question Architect Service', status: 'Operational', color: 'green' }
            ].map((service, idx) => (
              <div key={idx} className="flex items-center justify-between bg-white p-4 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <span className="font-medium text-gray-700">{service.name}</span>
                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                  service.color === 'green' 
                    ? 'bg-green-100 text-green-700 border border-green-200' 
                    : 'bg-red-100 text-red-700 border border-red-200'
                }`}>
                  <span className={`inline-block w-2 h-2 rounded-full mr-2 ${
                    service.color === 'green' ? 'bg-green-500' : 'bg-red-500'
                  }`}></span>
                  {service.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Pipeline Statistics */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl shadow-xl p-6 border-2 border-indigo-200">
          <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <span className="bg-blue-100 text-blue-600 rounded-lg p-2 mr-3">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </span>
            Pipeline Configuration
          </h3>
          <div className="space-y-4">
            {[
              { label: 'Relevance Threshold', value: '≥70', icon: '🎯', color: 'from-purple-500 to-pink-500' },
              { label: 'Vector Database', value: 'Qdrant', icon: '🗄️', color: 'from-blue-500 to-cyan-500' },
              { label: 'AI Model', value: 'Gemini 2.0 Flash', icon: '🤖', color: 'from-green-500 to-teal-500' },
              { label: 'Message Queue', value: 'Kafka', icon: '📡', color: 'from-orange-500 to-red-500' }
            ].map((stat, idx) => (
              <div key={idx} className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all hover:scale-105">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center text-2xl mr-4 shadow-lg`}>
                      {stat.icon}
                    </div>
                    <span className="font-medium text-gray-700">{stat.label}</span>
                  </div>
                  <span className="text-lg font-bold text-gray-900">{stat.value}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
